import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Settings, Wand2, Loader2, CheckCircle2, Shield, Users } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CompanyPanel() {
  const { isDark } = useTheme();
  const [aiEnabled, setAiEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/system/ai-config`);
      setAiEnabled(data.ai_enabled !== false);
    } catch { /* default enabled */ }
    try {
      const { data } = await axios.get(`${API_URL}/api/system/users`);
      setUsers(data);
    } catch { /* ok */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const toggleAI = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/system/ai-config`, { ai_enabled: !aiEnabled });
      setAiEnabled(!aiEnabled);
    } catch (err) {
      console.error(err);
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
        <CardContent className="p-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="company-panel">

      {/* Entitlements are NOT here. Semprion HQ signs in to the separate
          /admin console and never sees this tab, and this tab is
          company_support only — a role that cannot call /api/admin/customers.
          Plans and per-customer feature access live in AdminConsole's
          Customers and Billing panes. See docs/OL_ENTITLEMENTS.md. */}

      {/* AI Configuration */}
      <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
        <CardHeader className="pb-2">
          <CardTitle className={`flex items-center gap-2 text-lg ${isDark ? 'text-white' : 'text-slate-900'}`}>
            <Wand2 className="h-5 w-5 text-purple-500" />AI Configuration
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 rounded-lg border ${isDark ? 'bg-slate-700/30 border-slate-600' : 'bg-slate-50 border-slate-200'}">
            <div>
              <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>AI Headshot Processing</p>
              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                Gemini-powered photo cropping and formatting for player headshots
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Badge className={aiEnabled ? 'bg-green-600' : 'bg-red-600'}>
                {aiEnabled ? 'Enabled' : 'Disabled'}
              </Badge>
              <Button
                size="sm"
                variant={aiEnabled ? 'destructive' : 'default'}
                onClick={toggleAI}
                disabled={saving}
                className={!aiEnabled ? 'bg-green-600 hover:bg-green-700' : ''}
                data-testid="toggle-ai-btn"
              >
                {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : aiEnabled ? 'Disable' : 'Enable'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* User Access */}
      <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
        <CardHeader className="pb-2">
          <CardTitle className={`flex items-center gap-2 text-lg ${isDark ? 'text-white' : 'text-slate-900'}`}>
            <Users className="h-5 w-5 text-blue-500" />User Access ({users.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {users.map((u, i) => (
              <div key={i} className={`flex items-center justify-between px-3 py-2 rounded-lg ${isDark ? 'bg-slate-700/30' : 'bg-slate-50'}`} data-testid={`user-row-${i}`}>
                <div>
                  <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-800'}`}>{u.first_name} {u.last_name}</p>
                  <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{u.email}</p>
                </div>
                <div className="flex gap-1">
                  {u.roles?.map((r, j) => (
                    <Badge key={j} className={`text-xs ${
                      r.role_type === 'company_support' ? 'bg-red-600' :
                      r.role_type === 'ad' ? 'bg-blue-600' :
                      r.role_type === 'coach' ? 'bg-green-600' :
                      r.role_type === 'family' ? 'bg-cyan-600' :
                      'bg-slate-600'
                    }`}>{r.role_type}</Badge>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
